import { Badge, Button, Group, Modal, ScrollArea, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { Dropzone, FileRejection } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { IconCloudUpload, IconDownload, IconFileSpreadsheet, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateCSV } from '@mygpt/app/utils/file';

import { ParsedUrl, parseCsvText, parseExcelBuffer, validateAndNormalizeUrls } from './csvUrlParser';

interface CsvImportModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (urls: string[]) => void;
  existingUrls: string[];
}

const MAX_PREVIEW_ROWS = 100;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const CsvImportModal = ({ opened, onClose, onConfirm, existingUrls }: CsvImportModalProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [parsedUrls, setParsedUrls] = useState<ParsedUrl[]>([]);
  const [parsing, setParsing] = useState(false);

  const { validCount, invalidCount, duplicateCount } = useMemo(() => {
    let valid = 0,
      invalid = 0,
      duplicate = 0;
    for (const u of parsedUrls) {
      if (!u.isValid) invalid++;
      else if (u.isDuplicate) duplicate++;
      else valid++;
    }
    return { validCount: valid, invalidCount: invalid, duplicateCount: duplicate };
  }, [parsedUrls]);

  const handleReset = () => {
    setFile(null);
    setParsedUrls([]);
    setParsing(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleDownloadTemplate = () => {
    generateCSV('url_import_template', {
      header: ['URL'],
      rows: [
        ['https://example.com/page1'],
        ['https://example.com/page2'],
        ['https://example.com/docs/getting-started'],
      ],
    });
  };

  const handleFileDrop = async (files: File[]) => {
    const selectedFile = files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParsing(true);

    try {
      let rawUrls: string[];
      const ext = selectedFile.name.split('.').pop()?.toLowerCase();
      const isExcel =
        ext === 'xlsx' ||
        ext === 'xls' ||
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel';

      if (isExcel) {
        const buffer = await selectedFile.arrayBuffer();
        rawUrls = await parseExcelBuffer(buffer);
      } else {
        const text = await selectedFile.text();
        rawUrls = parseCsvText(text);
      }

      if (rawUrls.length === 0) {
        showNotification({
          message: t('create_v2.website.csv_import.empty_file'),
          color: 'orange',
        });
        setParsedUrls([]);
      } else {
        const parsed = validateAndNormalizeUrls(rawUrls, existingUrls);
        setParsedUrls(parsed);
      }
    } catch (error) {
      console.error('CSV parse error:', error);
      showNotification({
        message: t('create_v2.website.csv_import.empty_file'),
        color: 'red',
      });
      setParsedUrls([]);
    } finally {
      setParsing(false);
    }
  };

  const handleFileReject = (fileRejections: FileRejection[]) => {
    const { errors, file: rejectedFile } = fileRejections[0];
    const errorMsg = errors.map((e) => {
      const key = `errors.dropzone.${e.code}`;
      const translated = t(key);
      return translated === key ? e.message : translated;
    });
    showNotification({
      message: (
        <>
          <Text size="sm">{rejectedFile.name}</Text>
          <Text size="sm">{errorMsg.join(', ')}</Text>
        </>
      ),
      color: 'red',
    });
  };

  const handleConfirm = () => {
    const validUrls = parsedUrls.filter((u) => u.isValid && !u.isDuplicate).map((u) => u.normalized);
    onConfirm(validUrls);
    showNotification({
      message:
        duplicateCount > 0
          ? t('create_v2.website.csv_import.success_with_duplicates', {
              count: validUrls.length,
              duplicate: duplicateCount,
            })
          : t('create_v2.website.csv_import.success', { count: validUrls.length }),
      color: 'green',
    });
    handleReset();
  };

  const displayRows = parsedUrls.slice(0, MAX_PREVIEW_ROWS);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('create_v2.website.csv_import.modal_title')}
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* 下载模板 */}
        <UnstyledButton onClick={handleDownloadTemplate}>
          <Group gap={4}>
            <IconDownload size={14} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="blue.6">
              {t('create_v2.website.csv_import.download_template')}
            </Text>
          </Group>
        </UnstyledButton>

        {/* Dropzone 文件上传 */}
        <Dropzone
          onDrop={handleFileDrop}
          onReject={handleFileReject}
          loading={parsing}
          multiple={false}
          maxSize={MAX_FILE_SIZE}
          accept={{
            'text/csv': ['.csv'],
            'text/plain': ['.txt'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
          }}
          className="border-dashed border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
        >
          <Stack className="flex items-center justify-center min-h-[120px] pointer-events-none py-6" gap="sm">
            {file ? (
              <>
                <IconFileSpreadsheet size={36} className="text-blue-500" />
                <Text size="sm" c="blue.6" fw={500}>
                  {file.name}
                </Text>
              </>
            ) : (
              <>
                <Dropzone.Accept>
                  <IconCloudUpload size={40} stroke={1.5} className="text-blue-500" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={40} stroke={1.5} className="text-red-500" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconCloudUpload size={40} stroke={1.5} className="text-gray-400" />
                </Dropzone.Idle>
                <Stack gap={2} className="text-center">
                  <Text size="sm" fw={500}>
                    {t('create_v2.website.csv_import.dropzone_title')}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {t('create_v2.website.csv_import.dropzone_desc')}
                  </Text>
                </Stack>
              </>
            )}
          </Stack>
        </Dropzone>

        {/* 预览表格 */}
        {parsedUrls.length > 0 && (
          <>
            <ScrollArea h={300}>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={50}>{t('create_v2.website.csv_import.column_row')}</Table.Th>
                    <Table.Th>{t('create_v2.website.csv_import.column_url')}</Table.Th>
                    <Table.Th w={100}>{t('create_v2.website.csv_import.column_status')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayRows.map((item, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {index + 1}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" className="break-all" c={!item.isValid ? 'red' : undefined}>
                          {item.raw}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {!item.isValid ? (
                          <Badge size="xs" color="red" variant="light">
                            {t('create_v2.website.csv_import.status_invalid')}
                          </Badge>
                        ) : item.isDuplicate ? (
                          <Badge size="xs" color="orange" variant="light">
                            {t('create_v2.website.csv_import.status_duplicate')}
                          </Badge>
                        ) : (
                          <Badge size="xs" color="green" variant="light">
                            {t('create_v2.website.csv_import.status_valid')}
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            {/* 统计信息 */}
            <Group justify="space-between">
              <Text size="sm">
                {t('create_v2.website.csv_import.summary', {
                  valid: validCount,
                  invalid: invalidCount,
                  duplicate: duplicateCount,
                })}
              </Text>
              {parsedUrls.length > MAX_PREVIEW_ROWS && (
                <Text size="xs" c="dimmed">
                  {t('create_v2.website.csv_import.summary_showing', {
                    shown: MAX_PREVIEW_ROWS,
                    total: parsedUrls.length,
                  })}
                </Text>
              )}
            </Group>
          </>
        )}

        {/* 操作按钮 */}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>
            {t('create_v2.website.csv_import.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={validCount === 0}>
            {t('create_v2.website.csv_import.confirm', { count: validCount })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
